import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../api_config";
import UserContext from "../../Components/Authcontext";
import "./modern-forms.css";
import Load from "../Loading/Load";
import Select from "react-select";

function ModernHoroscope() {
  const { user, token } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [dob, setDOB] = useState("");
  const [place, setPlace] = useState("");
  const [meetingTime, setMeetingTime] = useState("");
  const [period, setPeriod] = useState("AM");
  const [selectedOptions, setSelectedOptions] = useState({});
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // Define the form steps
  const totalSteps = 9;
  const currentStep = 9;

  // Calculate progress percentage
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Options for the horoscope boxes
  const options = [
    { value: "ல", label: "ல" },
    { value: "ரா", label: "ரா" },
    { value: "கே", label: "கே" },
    { value: "சூ", label: "சூ" },
    { value: "சனி", label: "சனி" },
    { value: "செ", label: "செ" },
    { value: "சுக்", label: "சுக்" },
    { value: "புதன்", label: "புதன்" },
    { value: "குரு", label: "குரு" },
    { value: "சந்திரன்", label: "சந்திரன்" },
    { value: "(மா )", label: "(மா )" },
    { value: "(வ )", label: "(வ )" },
  ];

  // Helper function for consistent select component styling and behavior
  const HoroscopeSelect = ({ name, value, onChange }) => (
    <Select
      name={name}
      options={options}
      isMulti
      placeholder="Select..."
      value={value}
      onChange={onChange}
      className="horoscope-select"
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        control: (base) => ({ ...base, minHeight: '32px' }),
        valueContainer: (base) => ({ ...base, padding: '0 8px' }),
        multiValue: (base) => ({ 
          ...base, 
          backgroundColor: 'rgba(255, 102, 0, 0.1)',
          margin: '2px'
        })
      }}
    />
  );

  useEffect(() => {
    const fetchHoroscopeData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/horoscope`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });
        
        if (response.data && response.data.length > 0) {
          const horoscopeData = response.data[0];

          // Process box selections
          const selectedOptionsObj = {};
          for (let i = 1; i <= 24; i++) {
            const boxName = `box${i}`;
            const selectedOption = horoscopeData[boxName];
            if (selectedOption && selectedOption.length > 0) {
              selectedOptionsObj[i] = selectedOption.split(",").map((option) => ({
                value: option,
                label: option,
              }));
            }
          }
          
          setSelectedOptions(selectedOptionsObj);
          
          // Format and set date
          if (horoscopeData.date) {
            const dobDate = new Date(horoscopeData.date);
            const year = dobDate.getFullYear();
            const month = String(dobDate.getMonth() + 1).padStart(2, "0");
            const day = String(dobDate.getDate()).padStart(2, "0");
            setDOB(`${year}-${month}-${day}`);
          }
          
          // Set other fields
          setPlace(horoscopeData.place || "");
          setMeetingTime(horoscopeData.time || "");
          setPeriod(horoscopeData.period || "AM");
        }
      } catch (error) {
        console.error("Error fetching horoscope data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHoroscopeData();
  }, [token]);

  const handleDOBChange = (e) => {
    setDOB(e.target.value);
  };

  const handleTimeChange = (e) => {
    setMeetingTime(e.target.value);
  };

  const handlePlaceChange = (e) => {
    setPlace(e.target.value);
  };

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  const handleChange = (selected, name) => {
    setSelectedOptions((prevSelectedOptionsMap) => ({
      ...prevSelectedOptionsMap,
      [name]: selected,
    }));
  };

  const getSelectedOptions = (name) => {
    return selectedOptions[name] || null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Process selected options for API submission
      const selectedOptionsObj = {};

      for (let i = 1; i <= 24; i++) {
        const boxName = `${i}`;
        const selected = selectedOptions[i];

        if (selected && selected.length > 0) {
          selectedOptionsObj[boxName] = selected.map(option => option.value);
        }
      }

      // Match the format of the original Horo.js component
      const response = await axios.post(
        `${API_BASE_URL}/api/insertHoroscope`,
        { 
          user, 
          selectedOptionsObj, 
          dob, 
          place, 
          meetingTime, 
          period 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true
        }
      );

      console.log("API Response:", response.data);

      if (response.status === 200) {
        setLoading(false);
        navigate("/user-under-verification", { replace: true });
      } else {
        setLoading(false);
        setError("Failed to save horoscope information");
      }
    } catch (error) {
      console.error("API Error:", error);
      setLoading(false);
      setError(error.response?.data?.message || error.message || "Failed to save horoscope information");
    }
  };

  return loading ? (
    <Load />
  ) : (
    <div className="profile-form-container">
      <div className="decoration decoration-1"></div>
      <div className="decoration decoration-2"></div>
      <div className="decoration decoration-3"></div>
      <div className="decoration decoration-4"></div>
      <div className="profile-form-card">
        {/* Form Header with Progress Bar */}
        <div className="profile-form-header">
          <div className="progress-container">
            <div className="progress-steps">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div key={i} className="progress-step">
                  <div
                    className={`step-number ${i + 1 === currentStep ? "active" : ""} ${
                      i + 1 < currentStep ? "completed" : ""
                    }`}
                  >
                    {i + 1 < currentStep ? "\u2713" : i + 1}
                  </div>
                  <div
                    className={`step-label ${i + 1 === currentStep ? "active" : ""} ${
                      i + 1 < currentStep ? "completed" : ""
                    }`}
                  >
                    {i + 1 === 1 && "Basic Info"}
                    {i + 1 === 2 && "Contact"}
                    {i + 1 === 3 && "Personal"}
                    {i + 1 === 4 && "Photos"}
                    {i + 1 === 5 && "Family"}
                    {i + 1 === 6 && "Lifestyle"}
                    {i + 1 === 7 && "Religious"}
                    {i + 1 === 8 && "Preferences"}
                    {i + 1 === 9 && "Horoscope"}
                  </div>
                </div>
              ))}
              <div className="progress-line">
                <div
                  className="progress-line-fill"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
          <div className="form-header-content">
            <h2 className="profile-form-title">Horoscope Details</h2>
            <p className="profile-form-subtitle">Enter your birth and horoscope information</p>
            <div className="form-header-info">
              <div className="info-badge">
                <span className="info-icon">ℹ️</span>
                <span className="info-text">This information helps in astrological matching</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit}>
          <div className="profile-form-body">
            {/* Birth Information Section */}
            <div className="form-section-title">Birth Information</div>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={dob} 
                    onChange={handleDOBChange} 
                    required 
                  />
                </div>
              </div>
              <div className="form-col">
                <div className="form-group">
                  <label className="form-label">Time of Birth</label>
                  <div className="time-input-group">
                    <input 
                      type="time" 
                      className="form-control time-input" 
                      value={meetingTime} 
                      onChange={handleTimeChange} 
                      required 
                    />
                    <select 
                      className="form-control period-select" 
                      value={period} 
                      onChange={handlePeriodChange}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Place of Birth</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Enter your birth place" 
                value={place} 
                onChange={handlePlaceChange} 
                required 
              />
            </div>

            {/* Horoscope Grid Section */}
            <div className="form-section-title">Horoscope Charts</div>
            <div className="horoscope-grid-container">
              <div className="horoscope-charts-wrapper">
                {/* First Horoscope Rashi Chart (4x4 grid) */}
                <div className="horoscope-chart">
                  <h4 className="horoscope-chart-title">Rasi Chart</h4>
                  <div className="horoscope-grid">
                    {/* { Row - 1 } */}
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="1" value={getSelectedOptions("1")} onChange={(selected) => handleChange(selected, "1")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="2" value={getSelectedOptions("2")} onChange={(selected) => handleChange(selected, "2")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="3" value={getSelectedOptions("3")} onChange={(selected) => handleChange(selected, "3")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="4" value={getSelectedOptions("4")} onChange={(selected) => handleChange(selected, "4")} />
                    </div>

                    {/* { Row - 2 } */}
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="5" value={getSelectedOptions("5")} onChange={(selected) => handleChange(selected, "5")} />
                    </div>
                    <div className="horoscope-cell horoscope-center" style={{ gridColumn: "2 / span 2", gridRow: "2 / span 2" }}>
                      <div className="horoscope-center-text">
                        <h3>ராசி</h3>
                      </div>
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="6" value={getSelectedOptions("6")} onChange={(selected) => handleChange(selected, "6")} />
                    </div>
                    
                    {/* Row - 3 */}
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="7" value={getSelectedOptions("7")} onChange={(selected) => handleChange(selected, "7")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="8" value={getSelectedOptions("8")} onChange={(selected) => handleChange(selected, "8")} />
                    </div>

                    {/* Row - 4 */}
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="9" value={getSelectedOptions("9")} onChange={(selected) => handleChange(selected, "9")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="10" value={getSelectedOptions("10")} onChange={(selected) => handleChange(selected, "10")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="11" value={getSelectedOptions("11")} onChange={(selected) => handleChange(selected, "11")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="12" value={getSelectedOptions("12")} onChange={(selected) => handleChange(selected, "12")} />
                    </div>
                  </div>
                </div>

                {/* Second Horoscope Navamsa Chart (4x4 grid) */}
                <div className="horoscope-chart">
                  <h4 className="horoscope-chart-title">Navamsa Chart</h4>
                  <div className="horoscope-grid">
                    {/* Row - 1 */}
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="13" value={getSelectedOptions("13")} onChange={(selected) => handleChange(selected, "13")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="14" value={getSelectedOptions("14")} onChange={(selected) => handleChange(selected, "14")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="15" value={getSelectedOptions("15")} onChange={(selected) => handleChange(selected, "15")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="16" value={getSelectedOptions("16")} onChange={(selected) => handleChange(selected, "16")} />
                    </div>

                    {/* Row - 2 */}
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="17" value={getSelectedOptions("17")} onChange={(selected) => handleChange(selected, "17")} />
                    </div>

                    <div className="horoscope-cell horoscope-center" style={{ gridColumn: "2 / span 2", gridRow: "2 / span 2" }}>
                      <div className="horoscope-center-text">
                        <h3>நவம்சம்</h3>
                      </div>
                    </div>

                    <div className="horoscope-cell">
                      <HoroscopeSelect name="18" value={getSelectedOptions("18")} onChange={(selected) => handleChange(selected, "18")} />
                    </div>
                    
                    {/* Row - 3 */}
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="19" value={getSelectedOptions("19")} onChange={(selected) => handleChange(selected, "19")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="20" value={getSelectedOptions("20")} onChange={(selected) => handleChange(selected, "20")} />
                    </div>

                    
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="21" value={getSelectedOptions("21")} onChange={(selected) => handleChange(selected, "21")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="22" value={getSelectedOptions("22")} onChange={(selected) => handleChange(selected, "22")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="23" value={getSelectedOptions("23")} onChange={(selected) => handleChange(selected, "23")} />
                    </div>
                    <div className="horoscope-cell">
                      <HoroscopeSelect name="24" value={getSelectedOptions("24")} onChange={(selected) => handleChange(selected, "24")} />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <div className="info-badge">
                  <span className="info-icon">ℹ️</span>
                  <span className="info-text">Select the appropriate symbols in each box according to your horoscope. You can select multiple symbols per box.</span>
                </div>
              </div>
            </div>

            <div className="photo-guidelines">
              <h4 className="guidelines-title">Why is horoscope information important?</h4>
              <ul className="guidelines-list">
                <li>Helps in matching astrological compatibility</li>
                <li>Important for traditional families who consider horoscope matching</li>
                <li>Provides additional information for potential matches</li>
                <li>Can be a deciding factor for many families in the matrimony process</li>
              </ul>
            </div>
          </div>

          {/* Form Footer with Buttons */}
          <div className="profile-form-footer">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={() => navigate("/modern-partner-preference-info")}
            >
              Back
            </button>
            <button type="submit" className="btn-primary">
              Save Profile
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModernHoroscope;
